/**
 * A message body, rendered.
 *
 * Bodies are PLAIN TEXT everywhere else in the system: they are typed into a
 * textarea, edited before they send, and stored as text. This is the one place
 * that gives them shape, and it does it with a single rule rather than a
 * markdown parser: a run of consecutive lines starting with `- ` becomes a real
 * list, everything else keeps its pre-wrap paragraphs.
 *
 * That rule is what the generated ask / decision templates write
 * (`lib/messages.ts`), so their figure lines read as dot points in a
 * proportional-font bubble instead of the space-padded wall they used to be
 * (owner feedback 2026-08-05). A hand-typed message that happens to use `- `
 * gets the same treatment, which is the point.
 *
 * `maxLines` is for previews (the waiting-state activity trail): it clamps on
 * whole lines and marks the cut, rather than `line-clamp`, which does not clamp
 * a box whose children are block-level.
 *
 * NOT a second `SimpleMarkdown` (features/organization/.../agent-api). That one
 * presents a TRUSTED, in-repo authored document and reads a real block grammar
 * (headings, fences, tables, bold). This reads text a human typed into a chat
 * box, where markdown syntax is not intended: a `# ` or a `| ` line here is
 * someone's sentence, and rendering it as a heading or a table would be a bug.
 * One construct, deliberately.
 */
import { cn } from '@/lib/utils';

const BULLET = '- ';

interface Block {
  kind: 'bullets' | 'text';
  lines: string[];
  /** A blank line stood between this block and the one before it. */
  spaced: boolean;
}

/**
 * Split a body into bullet runs and paragraphs.
 *
 * Blank lines carry NO content — they become the gap between blocks, so the
 * spacing is the same 4px-grid rhythm everywhere instead of an empty line
 * here and a margin there. Single newlines inside a paragraph survive as
 * pre-wrap, because someone who hard-wrapped their own sentence meant it.
 */
function parseBlocks(body: string): Block[] {
  const blocks: Block[] = [];
  let gap = false;
  for (const raw of body.split('\n')) {
    if (raw.trim().length === 0) {
      gap = blocks.length > 0;
      continue;
    }
    const isBullet = raw.startsWith(BULLET);
    const kind: Block['kind'] = isBullet ? 'bullets' : 'text';
    const line = isBullet ? raw.slice(BULLET.length).trim() : raw;
    const last = blocks[blocks.length - 1];
    if (last && last.kind === kind && !gap) last.lines.push(line);
    else blocks.push({ kind, lines: [line], spaced: gap });
    gap = false;
  }
  return blocks;
}

/** Keep whole lines, then mark the cut. Never slices a line in half. */
function clampBlocks(blocks: Block[], maxLines: number): Block[] {
  const total = blocks.reduce((sum, block) => sum + block.lines.length, 0);
  if (total <= maxLines) return blocks;

  const kept: Block[] = [];
  let used = 0;
  for (const block of blocks) {
    if (used >= maxLines) break;
    const lines = block.lines.slice(0, maxLines - used);
    used += lines.length;
    kept.push({ ...block, lines });
  }

  const lastBlock = kept[kept.length - 1];
  const lastIndex = (lastBlock?.lines.length ?? 0) - 1;
  const lastLine = lastBlock?.lines[lastIndex];
  if (lastBlock && lastLine != null) lastBlock.lines[lastIndex] = `${lastLine}…`;
  return kept;
}

interface MessageBodyProps {
  body: string;
  /** Preview mode: keep at most this many lines and mark the cut. */
  maxLines?: number;
  className?: string;
}

export function MessageBody({ body, maxLines, className }: MessageBodyProps) {
  const parsed = parseBlocks(body);
  const blocks = maxLines == null ? parsed : clampBlocks(parsed, maxLines);

  return (
    <div className={className}>
      {blocks.map((block, index) => {
        // A list that follows its own lead-in sentence belongs to it, so it
        // sits closer than two paragraphs do.
        const spacing = index === 0 ? undefined : block.spaced ? 'mt-2' : 'mt-1';
        if (block.kind === 'text') {
          return (
            <p key={index} className={cn('whitespace-pre-wrap break-words', spacing)}>
              {block.lines.join('\n')}
            </p>
          );
        }
        return (
          <ul
            key={index}
            // pl-4 keeps the marker inside the bubble's own padding.
            className={cn('list-disc pl-4 marker:text-muted-foreground', spacing)}
          >
            {block.lines.map((line, lineIndex) => (
              <li key={lineIndex} className="break-words">
                {line}
              </li>
            ))}
          </ul>
        );
      })}
    </div>
  );
}
